import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import './InstantDropReport.scss';
import ExperimentDtoClient from '../../business/models/ExperimentDtoClient';
import pdfInstantDropReport from './PdfInstantDropReport';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { Plate, WellNavigationDetails } from 'components/Experiment/constants';
import { useSelectedDropImageParameters } from 'redux/hooks/experiment';
import { useExperimentReady } from './hooks';

type InstantDropReportProps = {
  docData: ExperimentDtoClient;
  selectedWell: WellNavigationDetails[];
  setOkayDisabled: any;
};

export const InstantDropReport = forwardRef((props: InstantDropReportProps, ref) => {
  const [drops, setDrops] = useState([]);
  const selectedDropImageParameters = useSelectedDropImageParameters();

  const populatingWellList = useExperimentReady(props.docData);

  useEffect(() => {
    const dropsArr = [];
    props.docData.containerDrops.forEach((element) => {
      const drop = {};
      drop['name'] = 'drop' + element.dropNumber;
      drop['dropNumber'] = element.dropNumber;
      drop['checked'] = element.dropNumber === selectedDropImageParameters.dropNumber;
      dropsArr.push(drop);
    });
    setDrops(dropsArr);
  }, [props.docData.containerDrops]);

  useImperativeHandle(ref, () => ({
    generateAction,
  }));

  useEffect(() => {
    if (drops.some((x) => x.checked)) {
      props.setOkayDisabled(false);
    } else {
      props.setOkayDisabled(true);
    }
  }, [drops]);

  async function generateAction(saveReport: boolean, onPopupBlocked?: () => void) {
    let generateReport = false;
    const completeDocData = await populatingWellList;

    for (const wl of props.selectedWell) {
      const plateInfo: Plate = completeDocData.plateDto.find(
        (plate) => plate.plateId === wl.plateID,
      );
      if (!plateInfo.ingredients?.wellList?.length) {
        generateReport = false;
        showAlertSnackBar(
          EAlertType.WARNING,
          ESnackBarPosition.TOP,
          'Well data is not yet loaded.',
        );
        break;
      }
      const selectedWellInfo = plateInfo.ingredients.wellList.find(
        (well) => well.wellNumber === wl.wellNum,
      );

      for (const drop of drops.filter((x) => x['checked'])) {
        const selectedDrop = selectedWellInfo.dropList.find(
          (drp) => drp.dropNumber === drop['dropNumber'],
        );
        if (
          !selectedDrop ||
          !(selectedDrop.dropLayerList.length > 0) ||
          !selectedDrop.proteinFormulationID
        ) {
          generateReport = false;
          showAlertSnackBar(
            EAlertType.WARNING,
            ESnackBarPosition.TOP,
            'Not all selected Drops have Protein.',
          );
          break;
        } else {
          generateReport = true;
        }
      }
      if (!generateReport) {
        break;
      }
    }

    if (generateReport) {
      await pdfInstantDropReport(
        props.selectedWell,
        completeDocData,
        drops,
        selectedDropImageParameters,
        saveReport,
        onPopupBlocked,
      );
    }
  }

  function onChangeHandler(event) {
    const tempDrops = [...drops];
    tempDrops.filter((x) => x['name'] === event.target.name)[0].checked =
      event.target.checked;
    setDrops(tempDrops);
  }

  const renderDropsList = useMemo(() => {
    return drops.map((drop) => (
      <>
        <input
          id='instantdropreport-renderdroplist-input-wrapper'
          type="checkbox"
          name={drop['name']}
          className="idr-checkboxes"
          onChange={onChangeHandler}
          checked={drop['checked']}
        />
        <label id='instantdropreport-renderdroplist-label' htmlFor={drop['name']} className="idr-label">
          Drop {drop['dropNumber']}
        </label>
      </>
    ));
  }, [drops]);

  return (
    <div id='instantdropreport-content-wrapper' className="idr-wrapper-content">
      <div id='instantdropreport-content-message' className="idr-content">
        Please select drop number you want to include into the report.
      </div>
      <div id={`instantdropreport-content-input-${renderDropsList}`} className="idr-content-input">{renderDropsList}</div>
    </div>
  );
});
